import { Network } from '@capacitor/network'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'

import { configApi, errorMessagesApi } from '@/api/client'
import { useNotifications } from '@/composables/useNotifications'
import { router } from '@/router'
import mqtt from '@/sync/mqtt'

type ErrorMessage = {
  id: string
  title: string
  description: string
}

export const useStatusStore = defineStore('status', () => {
  const { notifyError, close } = useNotifications()

  const networkConnected = ref(false)
  const mqttConnected = ref(false)
  const errorMessages = ref<ErrorMessage[]>([])
  const hasTimeError = ref(false)

  onMounted(() => {
    Network.addListener('networkStatusChange', status => {
      if (status.connected) {
        networkConnected.value = true
      } else {
        networkConnected.value = false
      }
    })
  })

  const isConnected = computed(() => {
    return mqttConnected.value && networkConnected.value
  })

  async function updateCurrentStatus() {
    const status = await Network.getStatus()
    networkConnected.value = status.connected
    mqttConnected.value = mqtt.isConnected()
    if (networkConnected.value) {
      await checkDeviceTime()
    }
  }

  async function checkDeviceTime() {
    try {
      const { time: serverTime } = await configApi.getTime()
      const timeDifference = Math.abs(new Date(serverTime).getTime() - new Date().getTime())
      hasTimeError.value = timeDifference > 5 * 60 * 1000
    } catch {
      hasTimeError.value = false
    }
  }

  function updateMqttStatus(connected: boolean) {
    mqttConnected.value = connected
  }

  async function refreshErrorMessages(inputErrorMessages?: ErrorMessage[]) {
    const newErrorMessages = inputErrorMessages
      ? inputErrorMessages
      : await errorMessagesApi.getErrorMessages()

    const oldErrorIds = new Set(errorMessages.value.map(msg => msg.id))
    const newErrorIds = new Set(newErrorMessages.map(msg => msg.id))

    oldErrorIds.forEach(oldId => {
      if (!newErrorIds.has(oldId)) {
        close(`error-${oldId}`)
      }
    })

    newErrorMessages.forEach((errorMessage: ErrorMessage) => {
      if (!oldErrorIds.has(errorMessage.id)) {
        notifyError({
          id: `error-${errorMessage.id}`,
          title: errorMessage.title,
          description: errorMessage.description,
          important: true
        })
      }
    })

    errorMessages.value = newErrorMessages
  }

  watch(hasTimeError, () => {
    if (hasTimeError.value) {
      router.push({ name: 'wrongTime' })
    } else {
      router.push({ name: 'employees' })
    }
  })

  return {
    networkConnected,
    mqttConnected,
    errorMessages,
    hasTimeError,
    isConnected,
    updateCurrentStatus,
    updateMqttStatus,
    refreshErrorMessages
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStatusStore, import.meta.hot))
}
